import { handleUtiqConsentManager } from './consentManager/handleUtiqConsentManager';
import { initialiseConfig } from './setup/initialiseConfig';
import { initializeUtiqApiFunctions } from './setup/initializeUtiqApiFunctions';
import { handleDataClear } from './helpers/handleDataClear';
import { consoleLogger } from '@shared/logging/consoleLogger';
import { handleRevocationPopup } from './setup/handleRevocationPopup';
import { initializeUtiqNamespace } from './setup/initializeUtiqNamespace';
import {
  ConnectionEligibilityDataEntryName,
  UtiqConsentVersionEntryName,
  UtiqConsentVersionPreviousEntryName,
  UtiqStatusEntryName,
} from '@shared/constants/utiq';
import { getTemplateContent } from '@shared/ui/elements/getTemplateContent';
import { getValidatedBase64Cookie } from '@shared/cookies/getValidatedBase64Cookie';
import { checkConsentVersionCookieValue } from '@shared/cookies/checkConsentVersionCookieValue';
import { handleModalPopup } from '@utiqLoader/setup/handleModalPopup';
import { isUtiqInfoPage } from '@utiqLoader/helpers/isUtiqInfoPage';
import { getUtiqHost } from '@shared/getUtiqHost';
import { triggerEligibilityCheck } from '@machine/externalActions';
import { callEventListeners } from '@shared/events/callEventListeners';

export const initializeUtiqFlow = async ({
  environment,
}: {
  environment: string;
}) => {
  const utiqConsentStatusCookie = getValidatedBase64Cookie(
    document.cookie,
    UtiqStatusEntryName,
  );
  const utiqHost: string = getUtiqHost();
  const isInfoPage = await isUtiqInfoPage();
  const shouldStopCustomCMP =
    isInfoPage ||
    ((window.Utiq?.config?.CMP === 'none' ||
      window.Utiq?.config?.CMP === 'Didomi' ||
      window.Utiq?.config?.CMP === 'Optanon') &&
      utiqConsentStatusCookie !== 'true');

  const handleRevocationPopupCallback = () => handleRevocationPopup();
  const handleConsentWithModalCallback = () => handleModalPopup();

  const initialEligibilityData = JSON.parse(
    localStorage.getItem(ConnectionEligibilityDataEntryName)!,
  );

  const Utiq: UtiqEnvironment = initializeUtiqNamespace();

  initialiseConfig(utiqHost, Utiq);

  initializeUtiqApiFunctions(
    environment,
    handleRevocationPopupCallback,
    handleConsentWithModalCallback,
  );

  window.Utiq ||= {};
  window.Utiq.initialEligibilityValue = initialEligibilityData?.isEligible;
  window.Utiq.initialConsentStatusCookieValue = utiqConsentStatusCookie;

  /* If the consent is not granted, we clear the data just in case it is still in local storage for any reason, e.g. load times or refresh */
  if (utiqConsentStatusCookie === 'false') {
    handleDataClear();
    /* We make mno-precheck call only if consent is granted or was not checked yet */
  } else if (
    !shouldStopCustomCMP &&
    !(await triggerEligibilityCheck({
      trigger: 'load',
    }))
  ) {
    window.Utiq ||= {};
    window.Utiq.data ||= {};
    window.Utiq.data.showRevocationErrorMessage = true;
    window.Utiq.data.showErrorModal = true;
    window.Utiq.API ||= {};
    window.Utiq.API.revokeConsentWithPopup = handleRevocationPopupCallback;
    window.Utiq.API.showConsentModal = handleConsentWithModalCallback;
    callEventListeners('onFlowCompleted', 'Utiq', {});
    return;
  }

  handleUtiqConsentManager({
    isOtherConsentManagementPlatformSet:
      Boolean(Utiq.config!.CMP) || isInfoPage,
  });

  /*
   * check for consent_version and consent_version_previous
   * if consent_version cookie does not exist, load the data
   */
  const consentVersionCookie = getValidatedBase64Cookie(
    document.cookie,
    UtiqConsentVersionEntryName,
  );
  const consentVersionPreviousCookie = getValidatedBase64Cookie(
    document.cookie,
    UtiqConsentVersionPreviousEntryName,
  );
  window.Utiq ||= {};
  window.Utiq.initialConsentVersionCookie = consentVersionCookie;
  window.Utiq.initialConsentVersionPreviousCookie =
    consentVersionPreviousCookie;

  /*
   * check if the Utiq.data.popupContent is populated and if there is a consent_version cookie
   * if there are none, then load the templateData.json
   */
  if (!window.Utiq?.data?.popupContent && window.Utiq?.config?.CMP) {
    const templateData = await getTemplateContent();

    if (templateData === null) {
      consoleLogger.logInfo('Template is null. Stopping the Utiq flow...');
      return;
    }

    const consentVersion: number | undefined = checkConsentVersionCookieValue(
      consentVersionCookie,
      templateData?.version,
    );

    if (consentVersion === undefined) {
      consoleLogger.logError(
        `"version" attribute is missing from templateData.json so cannot proceed`,
      );
      return;
    }
  }
};
